import { FC, useContext, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Heading6 } from '@blueprint/ui';
import { NextRouter, useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';

import { SelectPropertyType } from './SelectPropertyType';
import { SelectBeds } from './SelectBeds';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectTransportMode } from './SelectTransportMode';
import {
    SearchContext,
    SearchPageType,
    SearchSource
} from '../../../search/SearchContext';
import { CloseIconWithText } from '../../../search/common';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '../../../reducers/filters';
import { selectCurrentSortOrder } from '../../../reducers/listingsSortOrder';
import {
    HeaderWrapper,
    ModalCloseIconWrapper,
    UpdateButton,
    LocationFilterWrapper,
    ModalContentWrapper,
    FindHomeTextWrapper,
    StickyWrapperUpdateButton,
    FilterDivider
} from './SearchInitiationMobileModal.styles';
import { BuyRentTabs } from '../../../homepage/components';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';

import { SelectRadius } from './SelectRadius';
import {
    SearchInitationWrapper,
    StyledButtonMobile,
    StyledButtonWrapperMobile,
    HousePricesLocationWrapper
} from '../SearchInitiation/SearchInitiation.styles';
import { searchHousePrices, searchProperties } from 'search/helpers';
import useSearchLocation from 'search/hooks/useSearchLocation';

const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

type Props = {
    showRadius?: boolean;
    enableSearchTypeTabs?: boolean;
    updateResultsText: string;
    headlineText?: string;
    headerTextOverride?: string;
    close: () => void;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
    excludePageSize?: boolean;
    onSearchClick?: (args: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filters: any;
        router: NextRouter;
    }) => void;
};

export const SearchInitiationMobileModal: FC<Props> = (props) => {
    const {
        showRadius = true,
        close,
        enableSearchTypeTabs = true,
        updateResultsText,
        headlineText,
        headerTextOverride,
        searchType,
        eventCategory,
        searchSource,
        excludePageSize = false,
        onSearchClick
    } = props;
    const router = useRouter();
    const { state, dispatch } = useContext(SearchContext);
    const { userAlertId } = state;

    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const { selectedValue: locationTextValue } =
        useSelector(locationTextSelector);
    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const filters = useSelector(filtersValuesSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const [tabSelected, setTabSelected] = useState<Tab>(searchType);

    const isForSale = enableSearchTypeTabs
        ? tabSelected === 'for-sale' || tabSelected === 'new-homes'
        : searchType === 'for-sale' || searchType === 'new-homes';
    const isHousePricesSearch = tabSelected === 'house-prices';

    const value = locationTextValue || locationValue;
    const locationText = useSearchLocation();

    const handleSearchProperties = () => {
        const finalSearchType = enableSearchTypeTabs ? tabSelected : searchType;
        const hasEnteredAValueOrSelectedFromDropdown = !!value;
        const eventLabel = hasEnteredAValueOrSelectedFromDropdown
            ? `${finalSearchType}-success`
            : `${finalSearchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const excludeRadius = !showRadius;

        if (!value) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        // TODO: Move up/down as necessary depending on whether different tracking required
        if (onSearchClick) {
            onSearchClick({
                filters,
                router
            });
            close();
            return;
        }

        searchProperties(
            userAlertId,
            router,
            dispatch,
            finalSearchType as SearchPageType,
            searchSource,
            filters,
            currentSortOrder,
            excludeRadius,
            excludePageSize
        );

        close();
    };

    const headerText = isForSale
        ? 'Search homes for sale'
        : 'Search homes to rent';

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const handleSearchHousePrices = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            // No failure label as the button is disabled if there is no content
            eventLabel: 'house-prices-success'
        });

        searchHousePrices(value, identifier);
        close();
    };

    const getHeadlineText = (tab: Tab) => {
        if (tab === 'house-prices') {
            // TODO: confirm this is correct
            return 'House prices';
        } else {
            return headlineText || headerText;
        }
    };

    const handleClose = () => {
        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_close'
        });

        close();
    };

    const showError = hasSubmittedLocationSearch && !value;

    return (
        <ModalContentWrapper>
            <HeaderWrapper>
                {!headlineText ? (
                    <Text isBold color="grey-slate">
                        {headerTextOverride || headerText}
                    </Text>
                ) : null}
                <ModalCloseIconWrapper>
                    <CloseIconWithText
                        onClick={handleClose}
                        testId="quicksearch-modal_close-button_icon-only"
                        text="Close"
                    />
                </ModalCloseIconWrapper>
            </HeaderWrapper>

            {enableSearchTypeTabs ? (
                <FindHomeTextWrapper>
                    <Heading6>{getHeadlineText(tabSelected)}</Heading6>
                </FindHomeTextWrapper>
            ) : null}

            {enableSearchTypeTabs ? (
                <BuyRentTabs
                    onPress={setTabSelected}
                    selected={tabSelected}
                    includeHousePrices
                    testIdSuffix={'mobile-modal'}
                />
            ) : null}

            {isHousePricesSearch ? (
                <SearchInitationWrapper hasExtraPaddingTop>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationText}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapperMobile>
                        <StyledButtonMobile
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="mobile-house-prices-search-button"
                            disabled={!value}
                        >
                            Search
                        </StyledButtonMobile>
                    </StyledButtonWrapperMobile>
                </SearchInitationWrapper>
            ) : (
                <>
                    <LocationFilterWrapper>
                        <LocationSearchInput
                            id="filters-location-mobile"
                            onLocationInputKeyDown={handleSearchProperties}
                            onlyDisplayRemoveIconWhenActive={false}
                            value={locationText}
                            showError={showError}
                        />
                    </LocationFilterWrapper>

                    <FilterDivider />

                    {isTravelTimeSearch ? (
                        <>
                            <SelectMaxTravelTime />
                            <FilterDivider />
                            <SelectTransportMode />{' '}
                        </>
                    ) : showRadius ? (
                        <SelectRadius />
                    ) : null}

                    <FilterDivider />

                    {isForSale ? <SelectPriceForSale /> : <SelectPriceToRent />}

                    <FilterDivider />

                    <SelectBeds />

                    <FilterDivider />

                    <SelectPropertyType
                        excludeFarms={searchSource === 'new-homes-dev'}
                    />
                    <StickyWrapperUpdateButton>
                        <UpdateButton
                            isFullWidth
                            type="button"
                            onClick={handlePressUpdate}
                            data-testid="update-results-button"
                            title="Update"
                        >
                            {updateResultsText}
                        </UpdateButton>
                    </StickyWrapperUpdateButton>
                </>
            )}
        </ModalContentWrapper>
    );
};
